import { useState, Fragment } from "react";
import { useClassifyQueue } from "../hooks/useClassifyQueue";
import type { BalanceParkedBatch, ClassifyItem, WaitingDoc } from "../api/client";
import { setCsResidency, deleteDocumentApi } from "../api/client";
import { BatchBalancePanel } from "../components/BatchBalancePanel";
import { ClassifyDetailPanel } from "../components/ClassifyDetailPanel";
import styles from "./ClassifyQueuePage.module.css";

/**
 * R-245 — Phase 5a Classification Gate page.
 *
 * Lists documents parked in the Classify pipeline step (awaiting a
 * human form-type selection).
 *
 * R-255 Phase 5d-1 (Task 4): converted to master-detail split layout.
 * F-076 — the row-level fast-path form picker + Confirm button were removed;
 * the detail pane (right) is now the ONLY classify path.
 * F-086 — the whole row is the click target (Enter/Space on a focused row
 * works too); the redundant View button/column is gone so the master pane
 * never scrolls horizontally.
 * Left: table of ClassifyRow.
 * Right: ClassifyDetailPanel for the selected document, or a placeholder.
 */

// F-086 — shared clickable-row contract (DESIGN.md §5.3a): the whole <tr> is
// the click target, keyboard-focusable, Enter/Space activates. Buttons inside
// rows stopPropagation their own clicks so they never also select the row.
function clickableRowProps(
  id: string,
  isSelected: boolean,
  onSelect: (id: string) => void,
) {
  return {
    "aria-selected": isSelected,
    tabIndex: 0,
    className: `${styles.rowClickable}${isSelected ? ` ${styles.rowSelected}` : ""}`,
    onClick: () => onSelect(id),
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.target !== e.currentTarget) return; // keys inside inputs/buttons
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(id);
      }
    },
  };
}

// ── helpers ────────────────────────────────────────────────────────────────

function formatTimeInStep(stepEnteredAt: string): string {
  const ms = Date.now() - new Date(stepEnteredAt).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    return `${days}d in step`;
  }
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours}h in step`;
  }
  return `${minutes}m in step`;
}

function formatConfidence(conf: number | null): string | null {
  if (conf === null) return null;
  return `${Math.round(conf * 100)}%`;
}

// ── Row ───────────────────────────────────────────────────────────────────

interface ClassifyRowProps {
  item: ClassifyItem;
  onSelect: (id: string) => void;
  isSelected: boolean;
}

function ClassifyRow({ item, onSelect, isSelected }: ClassifyRowProps) {
  const confidenceLabel = formatConfidence(item.classConfidence);
  const confClass =
    confidenceLabel === null
      ? styles.confidence
      : item.classConfidence! >= 0.8
        ? styles.confidenceHigh
        : styles.confidenceLow;

  return (
    <tr {...clickableRowProps(item.id, isSelected, onSelect)}>
      <td>
        {item.thumbnailPath ? (
          <img
            src={item.thumbnailPath}
            alt={`Thumbnail for ${item.filename}`}
            className={styles.thumbnail}
          />
        ) : (
          <div className={styles.thumbnailPlaceholder} aria-hidden="true" />
        )}
      </td>
      <td>
        <span className={styles.filename}>{item.filename}</span>
        {item.sequenceStatus === "misordered" && (
          <span className={styles.statusBadgeWarning}>Out of order</span>
        )}
        {item.displayId && (
          <>
            <br />
            <span className={styles.displayId}>{item.displayId}</span>
          </>
        )}
      </td>
      <td>
        <span className={styles.jobType}>{item.jobTypeName ?? "—"}</span>
      </td>
      <td>
        <span className={styles.timeInStep}>
          {formatTimeInStep(item.stepEnteredAt)}
        </span>
      </td>
      <td>
        <span className={confClass}>{confidenceLabel ?? "—"}</span>
      </td>
    </tr>
  );
}

// ── Residency Row (on-hold / pending-deletion buckets) ───────────────────────
// Renders filename, job type, and residency-specific actions spanning the
// time/confidence columns: Resume (research_hold) or Unflag [+ Purge]
// (pending_deletion). F-086 — the row itself opens the detail panel; action
// buttons stopPropagation so they never also select the row.

interface ResidencyRowProps {
  item: ClassifyItem;
  /** "research_hold" → show Resume; "pending_deletion" → show Unflag [+ Purge] */
  residency: "research_hold" | "pending_deletion";
  canDeleteInClassify: boolean;
  onDone: () => void;
  onSelect: (id: string) => void;
  isSelected: boolean;
}

function ResidencyRow({ item, residency, canDeleteInClassify, onDone, onSelect, isSelected }: ResidencyRowProps) {
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  async function handleResume() {
    setBusy(true);
    setRowError(null);
    try {
      await setCsResidency(item.id, "clear");
      onDone();
    } catch (e) {
      setRowError(e instanceof Error ? e.message : "Failed to resume hold.");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnflag() {
    setBusy(true);
    setRowError(null);
    try {
      await setCsResidency(item.id, "clear");
      onDone();
    } catch (e) {
      setRowError(e instanceof Error ? e.message : "Failed to unflag.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePurge() {
    setBusy(true);
    setRowError(null);
    try {
      await deleteDocumentApi(item.id);
      onDone();
    } catch (e) {
      setRowError(e instanceof Error ? e.message : "Failed to purge document.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr {...clickableRowProps(item.id, isSelected, onSelect)}>
      <td>
        {item.thumbnailPath ? (
          <img
            src={item.thumbnailPath}
            alt={`Thumbnail for ${item.filename}`}
            className={styles.thumbnail}
          />
        ) : (
          <div className={styles.thumbnailPlaceholder} aria-hidden="true" />
        )}
      </td>
      <td>
        <span className={styles.filename}>{item.filename}</span>
        {item.displayId && (
          <>
            <br />
            <span className={styles.displayId}>{item.displayId}</span>
          </>
        )}
      </td>
      <td>
        <span className={styles.jobType}>{item.jobTypeName ?? "—"}</span>
      </td>
      <td colSpan={2}>
        <div className={styles.actions}>
          {residency === "research_hold" && (
            <button
              type="button"
              className={styles.btnSecondary}
              disabled={busy}
              aria-label={`Resume hold for ${item.filename}`}
              onClick={(e) => {
                e.stopPropagation(); // F-086 — don't also select the row
                void handleResume();
              }}
            >
              Resume
            </button>
          )}
          {residency === "pending_deletion" && (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={busy}
                aria-label={`Unflag pending deletion for ${item.filename}`}
                onClick={(e) => {
                  e.stopPropagation(); // F-086 — don't also select the row
                  void handleUnflag();
                }}
              >
                Unflag
              </button>
              {canDeleteInClassify && (
                <button
                  type="button"
                  className={styles.btnDanger}
                  disabled={busy}
                  aria-label={`Purge document ${item.filename}`}
                  onClick={(e) => {
                    e.stopPropagation(); // F-086 — don't also select the row
                    void handlePurge();
                  }}
                >
                  Purge
                </button>
              )}
            </>
          )}
        </div>
        {rowError && <p className={styles.rowError}>{rowError}</p>}
      </td>
    </tr>
  );
}

// ── Batch Group Header ───────────────────────────────────────────────────

interface BatchGroupHeaderProps {
  batchLabel: string;
  fixCount: number;
  waitingDocs: WaitingDoc[];
  expanded: boolean;
  onToggle: () => void;
  /** R-264 5e-2 — set when the batch is parked in balance_review. */
  balance?: BalanceParkedBatch;
  onBalanceChanged?: () => void;
  /** R-268 6f — count of parked-unidentified docs eligible for re-score. */
  reresolvableCount?: number;
  /** R-268 6f — trigger re-score for this batch. */
  onRecheck?: () => void;
}

function BatchGroupHeader({
  batchLabel,
  fixCount,
  waitingDocs,
  expanded,
  onToggle,
  balance,
  onBalanceChanged,
  reresolvableCount = 0,
  onRecheck,
}: BatchGroupHeaderProps) {
  return (
    <>
      <tr className={styles.batchGroupHeader}>
        <td colSpan={5}>
          <div className={styles.batchGroupHeaderInner}>
            <span className={styles.batchGroupLabel}>
              {batchLabel}
              {balance && (
                <span className={styles.statusBadgeError}>Out of balance</span>
              )}
              <span className={styles.batchGroupCounts}>
                {/* R-264: "0 to fix" is misleading on a balance-parked batch —
                    the badge already says what's wrong, so omit the count. */}
                {fixCount > 0 && <> — {fixCount} to fix</>}
                {waitingDocs.length > 0 && (
                  <>
                    {fixCount > 0 ? " · " : " — "}
                    {waitingDocs.length} waiting
                  </>
                )}
                {/* Intentional: "· 0 waiting" and the chevron are hidden when
                    waiting is empty — a batch with 0 held docs has nothing to
                    expand, so the toggle button would be misleading. Exception:
                    a balance-parked batch always gets the toggle (it expands
                    to the balance panel). */}
              </span>
            </span>
            <div className={styles.batchGroupActions}>
              {/* R-268 6f — re-score parked unidentified docs when count > 0 */}
              {reresolvableCount > 0 && (
                <button
                  type="button"
                  className={styles.recheckButton}
                  onClick={onRecheck}
                  title="Re-score unidentified documents against newly-learned fingerprints"
                >
                  Re-check unidentified ({reresolvableCount})
                </button>
              )}
              {(waitingDocs.length > 0 || balance) && (
                <button
                  type="button"
                  className={styles.btnSecondary}
                  aria-label="Show batch contents"
                  onClick={onToggle}
                >
                  {expanded ? "▲" : "▼"}
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>
      {expanded && balance && (
        <tr className={styles.balancePanelRow}>
          <td colSpan={5}>
            <BatchBalancePanel
              batch={balance}
              onChanged={onBalanceChanged ?? (() => {})}
            />
          </td>
        </tr>
      )}
      {expanded &&
        waitingDocs.map((w) => (
          <tr key={w.id} className={styles.waitingRow}>
            <td />
            <td>
              <span className={styles.waitingDocName}>
                {w.displayId ?? w.filename}
              </span>
            </td>
            <td colSpan={2}>
              <span className={styles.waitingDocMeta}>
                {w.formName ?? "—"} · {w.pageCount} pages
              </span>
            </td>
            <td>
              <span className={styles.waitingBadge}>waiting</span>
            </td>
          </tr>
        ))}
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export function ClassifyQueuePage() {
  // balanceParked/reresolvable/canDeleteInClassify defaults guard older hook mocks that omit the fields.
  const {
    items,
    waiting,
    balanceParked = [],
    reresolvable = {},
    canDeleteInClassify = false,
    isLoading,
    error,
    refresh,
    recheckUnidentified,
  } = useClassifyQueue();
  // F-041 — when the Queue Dashboard claims-next into a classify lane it deep-
  // links here with ?documentId=, so the claimed doc is preselected on arrival.
  // Read directly from the URL (not useSearchParams) to stay router-context-free.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("documentId");
  });
  // R-262 5d-5b — tracks which batch group headers are expanded
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  function handleResolved() {
    setSelectedId(null);
    refresh();
  }

  function toggleBatch(batchId: string) {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  }

  // CS-2 — partition by csResidency before batch/flat grouping.
  // Only needsAttention items feed into the actionable queue rendering below.
  //
  // Defensive partition: anything that is NOT explicitly "research_hold" or
  // "pending_deletion" (including null and any future/unknown value) lands in
  // needsAttention — guaranteeing no document can vanish from all three sections.
  const needsAttention = items.filter(
    (i) => i.csResidency !== "research_hold" && i.csResidency !== "pending_deletion",
  );
  const onHold = items.filter((i) => i.csResidency === "research_hold");
  const pendingDeletion = items.filter((i) => i.csResidency === "pending_deletion");

  // R-262 — batch-mode items group under a batch header; flow/hybrid stay flat.
  // Applied only to needsAttention (the actionable subset).
  const grouped = new Map<string, ClassifyItem[]>();
  const flat: ClassifyItem[] = [];
  for (const item of needsAttention) {
    if (item.processingMode === "batch") {
      const existing = grouped.get(item.batchId);
      if (existing) {
        existing.push(item);
      } else {
        grouped.set(item.batchId, [item]);
      }
    } else {
      flat.push(item);
    }
  }

  // R-264 5e-2 — balance_review batches. Batches that also have exception
  // items get the badge on their existing group header; parked batches with
  // no exception items render as additional groups after the grouped entries.
  const parkedById = new Map(balanceParked.map((b) => [b.batchId, b]));
  const parkedOnly = balanceParked.filter((b) => !grouped.has(b.batchId));
  // hasRows: true when there is ANYTHING to show — items covers all residency states
  // (actionable, held, pending); parkedOnly covers balance-review batches with no exceptions.
  const hasRows = items.length > 0 || parkedOnly.length > 0;

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Classification Queue</h1>
      <p className={styles.subhead}>
        Documents parked awaiting a form-type selection. Pick a form and confirm
        to route each document onward.
      </p>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.masterDetail}>
        {/* ── Master (left) ─────────────────────────────────────── */}
        <div className={styles.master}>
          {isLoading && <p className={styles.loading}>Loading…</p>}

          {!isLoading && !hasRows && !error && (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateTitle}>No documents awaiting classification</p>
              <p className={styles.emptyStateBody}>
                Documents appear here when they reach the Classification step and
                need a form type assigned.
              </p>
            </div>
          )}

          {!isLoading && hasRows && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Preview</th>
                  <th scope="col">Document</th>
                  <th scope="col">Job Type</th>
                  <th scope="col">Time in Step</th>
                  <th scope="col">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {/* CS-2 — "Needs attention" section header (always rendered when table is shown) */}
                <tr className={styles.bucketHeader}>
                  <td colSpan={5}>
                    <span className={styles.bucketHeaderLabel}>
                      Needs attention ({needsAttention.length})
                    </span>
                  </td>
                </tr>

                {/* Batch-mode groups — one header per batch, exception rows beneath */}
                {[...grouped.entries()].map(([batchId, groupItems]) => {
                  const firstItem = groupItems[0]!;
                  const waitingDocs = waiting[batchId] ?? [];
                  return (
                    <Fragment key={batchId}>
                      <BatchGroupHeader
                        batchLabel={firstItem.batchLabel}
                        fixCount={groupItems.length}
                        waitingDocs={waitingDocs}
                        expanded={expandedBatches.has(batchId)}
                        onToggle={() => toggleBatch(batchId)}
                        balance={parkedById.get(batchId)}
                        onBalanceChanged={refresh}
                        reresolvableCount={reresolvable[batchId] ?? 0}
                        onRecheck={() => void recheckUnidentified(batchId)}
                      />
                      {groupItems.map((item) => (
                        <ClassifyRow
                          key={item.id}
                          item={item}
                          onSelect={setSelectedId}
                          isSelected={item.id === selectedId}
                        />
                      ))}
                    </Fragment>
                  );
                })}
                {/* R-264 — balance_review batches with no exception items */}
                {parkedOnly.map((b) => (
                  <BatchGroupHeader
                    key={b.batchId}
                    batchLabel={b.batchLabel}
                    fixCount={0}
                    waitingDocs={waiting[b.batchId] ?? []}
                    expanded={expandedBatches.has(b.batchId)}
                    onToggle={() => toggleBatch(b.batchId)}
                    balance={b}
                    onBalanceChanged={refresh}
                    reresolvableCount={reresolvable[b.batchId] ?? 0}
                    onRecheck={() => void recheckUnidentified(b.batchId)}
                  />
                ))}
                {/* Flow/hybrid items — flat, no group header */}
                {flat.map((item) => (
                  <ClassifyRow
                    key={item.id}
                    item={item}
                    onSelect={setSelectedId}
                    isSelected={item.id === selectedId}
                  />
                ))}

                {/* CS-2 — "On hold · research" bucket with Resume inline action */}
                {onHold.length > 0 && (
                  <Fragment>
                    <tr className={styles.bucketHeader}>
                      <td colSpan={5}>
                        <span className={styles.bucketHeaderLabel}>
                          On hold · research ({onHold.length})
                        </span>
                      </td>
                    </tr>
                    {onHold.map((item) => (
                      <ResidencyRow
                        key={item.id}
                        item={item}
                        residency="research_hold"
                        canDeleteInClassify={canDeleteInClassify}
                        onDone={refresh}
                        onSelect={setSelectedId}
                        isSelected={item.id === selectedId}
                      />
                    ))}
                  </Fragment>
                )}

                {/* CS-2 — "Pending deletion" bucket with Unflag + optional Purge */}
                {pendingDeletion.length > 0 && (
                  <Fragment>
                    <tr className={styles.bucketHeader}>
                      <td colSpan={5}>
                        <span className={styles.bucketHeaderLabel}>
                          Pending deletion ({pendingDeletion.length})
                        </span>
                      </td>
                    </tr>
                    {pendingDeletion.map((item) => (
                      <ResidencyRow
                        key={item.id}
                        item={item}
                        residency="pending_deletion"
                        canDeleteInClassify={canDeleteInClassify}
                        onDone={refresh}
                        onSelect={setSelectedId}
                        isSelected={item.id === selectedId}
                      />
                    ))}
                  </Fragment>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Detail (right) ────────────────────────────────────── */}
        <div className={styles.detail}>
          {selectedId ? (
            <ClassifyDetailPanel
              documentId={selectedId}
              onResolved={handleResolved}
            />
          ) : (
            <div className={styles.detailPlaceholder}>
              Select a document to inspect.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
